/**
 * The progress bar in the Speedy player (Tim, 3 Nov: "no control to skip things"). A thin bar under
 * the title that shows how far the Speedy on screen has played. Press or drag anywhere on it to move
 * through the video: it thickens, shows a knob and the time ("1:42 / 5:06"), and the video follows
 * the finger. With the keyboard it is a slider: ← and → move 5 seconds, Home and End jump.
 *
 * It drives the `<video>` of the slide marked `data-speedy-current` (the same element the reel's
 * mute uses), so it needs nothing from the slide. It is marked `data-speedy-no-swipe` so dragging
 * across it never swipes to the next Speedy.
 */
import { useEffect, useRef, useState, type RefObject } from 'react';
import { formatSpeedyLength } from './SpeedyTile';

/** Seconds ← and → move. */
export const SCRUB_STEP_S = 5;

/** The video of the Speedy on screen inside `root`, if it has one yet. */
export function currentSpeedyVideo(root: HTMLElement | null): HTMLVideoElement | null {
  return root?.querySelector<HTMLVideoElement>('[data-speedy-current] video') ?? null;
}

/** Where a pointer at `clientX` falls along `rect`, as seconds of `duration`. */
export function timeAt(clientX: number, rect: { left: number; width: number }, duration: number): number {
  if (!rect.width || !duration) return 0;
  return Math.min(duration, Math.max(0, ((clientX - rect.left) / rect.width) * duration));
}

export function SpeedyScrubber({ rootRef, speedyId, fallbackDuration }: {
  rootRef: RefObject<HTMLElement>;
  /** Changes when the Speedy on screen changes, so the bar finds the new video. */
  speedyId: string;
  fallbackDuration: number | null;
}) {
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(fallbackDuration ?? 0);
  const [drag, setDrag] = useState<number | null>(null);
  const bar = useRef<HTMLDivElement>(null);

  // The slide mounts its video when its pass arrives: look until it's there.
  useEffect(() => {
    setVideo(null);
    setTime(0);
    setDuration(fallbackDuration ?? 0);
    let frame = 0;
    let tries = 0;
    const find = () => {
      const v = currentSpeedyVideo(rootRef.current);
      if (v) return setVideo(v);
      if (++tries < 600) frame = window.setTimeout(find, 100);
    };
    find();
    return () => window.clearTimeout(frame);
  }, [rootRef, speedyId, fallbackDuration]);

  useEffect(() => {
    if (!video) return;
    const sync = () => {
      setTime(video.currentTime || 0);
      if (Number.isFinite(video.duration) && video.duration > 0) setDuration(video.duration);
    };
    sync();
    video.addEventListener('timeupdate', sync);
    video.addEventListener('durationchange', sync);
    video.addEventListener('loadedmetadata', sync);
    video.addEventListener('seeked', sync);
    return () => {
      video.removeEventListener('timeupdate', sync);
      video.removeEventListener('durationchange', sync);
      video.removeEventListener('loadedmetadata', sync);
      video.removeEventListener('seeked', sync);
    };
  }, [video]);

  const seek = (t: number) => {
    if (!video || !duration) return;
    video.currentTime = Math.min(duration, Math.max(0, t));
    setTime(video.currentTime);
  };
  const at = (clientX: number) => (bar.current ? timeAt(clientX, bar.current.getBoundingClientRect(), duration) : 0);

  const shown = drag ?? time;
  const pct = duration ? (shown / duration) * 100 : 0;
  const ready = !!video && duration > 0;

  return (
    <div
      data-speedy-no-swipe
      ref={bar}
      role="slider"
      tabIndex={ready ? 0 : -1}
      aria-label="Position in the Speedy"
      aria-valuemin={0}
      aria-valuemax={Math.round(duration)}
      aria-valuenow={Math.round(shown)}
      aria-valuetext={`${formatSpeedyLength(shown) ?? '0:00'} of ${formatSpeedyLength(duration) ?? '0:00'}`}
      aria-disabled={!ready}
      onPointerDown={(e) => {
        if (!ready) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        const t = at(e.clientX);
        setDrag(t);
        seek(t);
      }}
      onPointerMove={(e) => {
        if (drag === null) return;
        const t = at(e.clientX);
        setDrag(t);
        seek(t);
      }}
      onPointerUp={() => setDrag(null)}
      onPointerCancel={() => setDrag(null)}
      onKeyDown={(e) => {
        if (!ready) return;
        const to = e.key === 'ArrowRight' ? time + SCRUB_STEP_S
          : e.key === 'ArrowLeft' ? time - SCRUB_STEP_S
          : e.key === 'Home' ? 0
          : e.key === 'End' ? duration
          : null;
        if (to === null) return;
        e.preventDefault();
        e.stopPropagation();
        seek(to);
      }}
      className="group relative -mx-1 flex h-6 cursor-pointer touch-none select-none items-center px-1 focus-visible:outline-none"
    >
      <div className={`relative w-full overflow-visible rounded-full bg-white/25 transition-[height] ${drag !== null ? 'h-1.5' : 'h-[3px] group-hover:h-1.5 group-focus-visible:h-1.5'}`}>
        <div className="absolute inset-y-0 left-0 rounded-full bg-white" style={{ width: `${pct}%` }} />
        <span
          className={`absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow transition-opacity ${drag !== null ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100'}`}
          style={{ left: `${pct}%` }}
          aria-hidden
        />
      </div>
      {drag !== null && (
        <span className="pointer-events-none absolute -top-7 -translate-x-1/2 rounded-md bg-black/70 px-2 py-0.5 text-xs font-semibold tabular-nums text-white" style={{ left: `${pct}%` }} aria-hidden>
          {formatSpeedyLength(shown)} / {formatSpeedyLength(duration)}
        </span>
      )}
    </div>
  );
}
